import {
  WebGLRenderer,
  Scene,
  Group,
  IcosahedronGeometry,
  TorusKnotGeometry,
  TorusGeometry,
  // OctahedronGeometry,
  MeshLambertMaterial,
  ConeGeometry,
  BoxGeometry,
  PerspectiveCamera,
  MeshPhongMaterial,
  Mesh,
  FlatShading,
  DoubleSide,
  SpotLight,
  JSONLoader,
  Vector3,
} from 'three'

import {
  turbulateVertices,
  addSpheresToVertices,
  addSpheresToVerticesLite,
  addCylindersToEdges,
  addCylindersToEdgesLite,
  updateCylinder,
  storeOriginalVertices,
  storeOriginalCentroids,
  explodeGeometry,
  safeClearTimeline
} from 'lib/3dUtils'

import PlatonicJSON from 'assets/3dobjects/platonic_scaled.json'
import Noise from './Noise'

import ExplodeModifier from './ExplodeModifier'
import TessellateModifier from './TessellateModifier'
// import ConvexGeometry from './ConvexGeometry'
import { TweenMax, Power0, Power1, Power2 } from 'gsap'
import TimelineMax from 'gsap/TimelineMax'
import $ from 'jquery'

const noiseGenerator = new Noise(Math.random())

const explodeModifier = new ExplodeModifier()
const tessellateModifier = new TessellateModifier(0)
const tessellateModifier2 = new TessellateModifier(5)
const tessellateModifier3 = new TessellateModifier(3)

class Scene3dAnimation {
  constructor () {
    const jsonLoader = new JSONLoader()

    this.fov = 30
    this.edges = null
    this.allowCameraRotation = true

    this.currentTimeline = null

    this.vertexMeshes = {}

    this.centroids = {}
    this.shapeClone = null

    this.container = document.getElementById('scene3d')
    this.renderer = new WebGLRenderer({ antialias: true, alpha: true })
    this.scene = new Scene()
    this.camera = new PerspectiveCamera(this.fov, window.innerWidth / window.innerHeight, 1, 100)
    this.camera.position.z = 30

    // Add lights
    this.light1 = new SpotLight(0xffffff, 3)
    this.light1.position.set(1000, 600, 200)
    this.scene.add(this.light1)

    // ---------------
    // Group Hierarchy
    // ---------------
    // rotationGroup
    // |__ baseGroup
    //     |__ shapesGroups (multiple)
    //         |__ shape (Mesh)
    //         |__ edgesGroup
    //         |__ verticesGroup

    this.rotationGroup = new Group()
    this.baseGroup = new Group()
    this.rotationGroup.add(this.baseGroup)

    this.shapeGroups = {
      icosahedron: new Group(),
      platonic: new Group(),
      box: new Group(),
      torus: new Group(),
      torusKnot: new Group(),
      frame: new Group(),
      cone: new Group()
    }

    const shapeMaterial = new MeshPhongMaterial({
      color: 0x0f1024,
      lights: true,
      shininess: 1000,
      shading: FlatShading,
      transparent: true,
      side: DoubleSide
    })

    // Icosahedron
    const icosahedronGeometry = new IcosahedronGeometry(5, 2)
    turbulateVertices(icosahedronGeometry, 0.75)
    const icosahedronShape = new Mesh(icosahedronGeometry, shapeMaterial)
    icosahedronShape.name = 'shape'
    this.shapeGroups.icosahedron.add(icosahedronShape)

    // Platonic
    const platonicGeometry = jsonLoader.parse(PlatonicJSON)
    platonicGeometry.geometry.scale(1.1, 1.1, 1.1)
    const platonicShape = new Mesh(platonicGeometry.geometry, shapeMaterial)
    platonicShape.name = 'shape'
    this.shapeGroups.platonic.add(platonicShape)
    this.shapeGroups.platonic.visible = false

    // Box
    let boxGeometry = new BoxGeometry(7, 7, 7, 2, 2, 2)
    boxGeometry.rotateX(Math.PI / 4)
    boxGeometry.rotateZ(Math.PI / 4)
    tessellateModifier.modify(boxGeometry)
    tessellateModifier.modify(boxGeometry)
    turbulateVertices(boxGeometry, 0.5)
    // coneGeometry = new ConvexGeometry(coneGeometry.vertices)
    const boxShape = new Mesh(boxGeometry, shapeMaterial)
    boxShape.name = 'shape'
    this.shapeGroups.box.add(boxShape)
    this.shapeGroups.box.visible = false

    // TorusKnot
    let torusKnotGeometry = new TorusKnotGeometry(3, 1.25, 30, 6, 2, 3)
    const torusKnotShape = new Mesh(torusKnotGeometry, shapeMaterial)
    torusKnotShape.name = 'shape'
    this.shapeGroups.torusKnot.add(torusKnotShape)
    this.shapeGroups.torusKnot.visible = false

    // Torus
    let torusGeometry = new TorusGeometry(5, 2, 4, 3, Math.PI * 2)
    torusGeometry.rotateX(Math.PI / -8)
    tessellateModifier2.modify(torusGeometry)
    tessellateModifier2.modify(torusGeometry)
    tessellateModifier2.modify(torusGeometry)
    tessellateModifier2.modify(torusGeometry)
    turbulateVertices(torusGeometry, 0.5)
    const torusShape = new Mesh(torusGeometry, shapeMaterial)
    torusShape.name = 'shape'
    this.shapeGroups.torus.add(torusShape)
    this.shapeGroups.torus.visible = false

    // Cone
    let frameGeometry = new TorusGeometry(5, 2, 4, 4, Math.PI * 2)
    frameGeometry.rotateX(Math.PI / -8)
    tessellateModifier2.modify(frameGeometry)
    tessellateModifier2.modify(frameGeometry)
    tessellateModifier2.modify(frameGeometry)
    tessellateModifier2.modify(frameGeometry)
    turbulateVertices(frameGeometry, 1)
    const frameShape = new Mesh(frameGeometry, shapeMaterial)
    frameShape.name = 'shape'
    this.shapeGroups.frame.add(frameShape)
    this.shapeGroups.frame.visible = false

    // Cone
    let coneGeometry = new ConeGeometry(6, 8, 9, 2)
    coneGeometry.rotateX(Math.PI)
    coneGeometry.translate(0, -1, 0)
    tessellateModifier3.modify(coneGeometry)
    tessellateModifier3.modify(coneGeometry)
    tessellateModifier3.modify(coneGeometry)
    tessellateModifier3.modify(coneGeometry)
    turbulateVertices(coneGeometry, 1)
    const coneShape = new Mesh(coneGeometry, shapeMaterial)
    coneShape.name = 'shape'
    this.shapeGroups.cone.add(coneShape)
    this.shapeGroups.cone.visible = false

    this.activeShape = this.shapeGroups.icosahedron.getObjectByName('shape')

    this.render = this.render.bind(this)
  }

  /* ======================================================================
   * HELPERS
   * ====================================================================== */

  toggleCameraRotation (allow) {
    this.allowCameraRotation = allow

    if (!allow) {
      TweenMax.to(this.rotationGroup.rotation, 0.6, { x: 0, y: 0 })
      TweenMax.to(this.camera.position, 0.6, { z: 30 })
    }
  }

  /**
   *
   */
  transitionToShape (shapeKeyTo = null) {
    if (!shapeKeyTo) {
      return
    }

    safeClearTimeline(this.currentTimeline)

    const shapeTo = this.shapeGroups[shapeKeyTo].getObjectByName('shape')

    // First we need to clone the geometry as it is right now,
    // because the vertices are being changed/animated
    // in the render loop

    // shape data
    const sd = {
      active: {
        shape: this.activeShape,
        wireframe: this.activeShape.parent.getObjectByName('wireframe')
      },
      next: {
        shape: shapeTo,
        wireframe: shapeTo.parent.getObjectByName('wireframe')
      }
    }

    const animVals = {
      explodeActive: sd.active.shape.userData.explode ? sd.active.shape.userData.explode : 0,
      explodeNext: sd.next.shape.userData.explode ? sd.next.shape.userData.explode : 1,
    }

    sd.next.shape.parent.visible = true
    sd.next.shape.visible = false
    sd.next.wireframe.visible = false

    let timeline
    const timelinePromise = new Promise((resolve, reject) => {
      timeline = new TimelineMax({
        onComplete: () => {
          this.activeShape = sd.next.shape
          resolve()
        }
      })
      this.currentTimeline = timeline
    })

    timeline
      // ACTIVE
      // Group Rotation
      .fromTo(
        sd.active.shape.parent.rotation,
        2,
        { y: 0 },
        { y: 4, ease: Power2.easeInOut },
        0
      )
      // Material Opacity
      .to(
        [sd.active.wireframe.material],
        1,
        {
          opacity: 0,
          ease: Power0.easeNone,
          onComplete: () => { sd.active.shape.parent.visible = false }
        },
        0
      )
      // Explosion
      .to(
        animVals,
        2,
        {
          explodeActive: 1,
          ease: Power2.easeInOut,
          onUpdate: function () {
            explodeGeometry(sd.active.shape, 25, animVals.explodeActive)
            sd.active.shape.userData.explode = animVals.explodeActive
          }
        },
        0
      )

      // NEXT
      // Group Rotation
      .fromTo(
        sd.next.shape.parent.rotation,
        2,
        { y: -4 },
        { y: 0, ease: Power2.easeInOut },
        0
      )
      // Material Opacity
      .to(
        [sd.next.wireframe.material],
        1,
        {
          opacity: 1,
          ease: Power0.easeNone,
          onStart: function () {
            sd.next.shape.visible = true
            sd.next.wireframe.visible = true
          }
        },
        1
      )
      // Explosion
      .to(
        animVals,
        2,
        {
          explodeNext: 0.00001,
          ease: Power2.easeInOut,
          onUpdate: function () {
            explodeGeometry(sd.next.shape, 25, animVals.explodeNext)
            sd.next.shape.userData.explode = animVals.explodeNext
          }
        },
        0
      )

    return timelinePromise
  }

  /**
   * Bursts the current shape.
   * Happens when user opens the site menu.
   */
  burstCurrentShape () {
    safeClearTimeline(this.currentTimeline)

    const shape = this.activeShape

    // TIMELINE
    let animVals = { explode: 0 }
    const timeline = new TimelineMax({ onUpdate: () => {
      shape.userData.explode = animVals.explode
    } })
    this.currentTimeline = timeline
    const childTimeline = new TimelineMax({ repeat: -1 })
    childTimeline
      .to(animVals, 3.2, {
        explode: 0.6,
        ease: Power1.easeInOut,
        onUpdate: function () {
          explodeGeometry(shape, 5, animVals.explode)
        }
      })
      .to(animVals, 3.2, {
        explode: 1,
        ease: Power1.easeInOut,
        onUpdate: function () {
          explodeGeometry(shape, 5, animVals.explode)
        }
      })

    timeline
      // Explosion
      .to(animVals, 0.8, {
        explode: 1,
        ease: Power2.easeInOut,
        onUpdate: function () {
          explodeGeometry(shape, 5, animVals.explode)
        }
      })
      .add(childTimeline)
  }

  /**
   * Joins/Implodes the current shape.
   * Happens when user closes the site menu.
   */
  joinCurrentShape () {
    safeClearTimeline(this.currentTimeline)

    const shape = this.activeShape

    let animVals = { explode: shape.userData.explode }
    const timeline = new TimelineMax()

    this.currentTimeline = timeline

    timeline
      .to(animVals, 0.8, {
        explode: 0.0001,
        ease: Power2.easeInOut,
        onUpdate: function () {
          explodeGeometry(shape, 5, animVals.explode)
        }
      })
  }

  /* ======================================================================
   * RENDER LOOP
   * ====================================================================== */

  render () {
    var t = (Date.now() / 200)
    this.baseGroup.rotation.y = t / 20
    this.renderer.render(this.scene, this.camera)
    requestAnimationFrame(this.render)
  }

  playIntroAnimation () {
    safeClearTimeline(this.currentTimeline)
    animCounter = 0

    const shape = this.activeShape
    this.animateVertices(shape)
    const shapeClone = new Mesh(this.activeShape.geometry.clone(), this.activeShape.material.clone())
    const edgeGroup = shape.parent.getObjectByName('edgesGroup')
    const verticesGroup = shape.parent.getObjectByName('verticesGroup')

    shapeClone.name = 'clone'
    shape.visible = false

    shapeClone.material.opacity = 0
    edgeGroup.children[0].material.opacity = 0
    verticesGroup.children[0].material.opacity = 0

    explodeModifier.modify(shapeClone.geometry)
    storeOriginalVertices(shapeClone)
    storeOriginalCentroids(shapeClone)
    shape.parent.add(shapeClone)

    explodeGeometry(shapeClone, 20, 1)

    // TIMELINE
    let animVals = { explode: 1, opacity: 0, scale: 0 }
    const timeline = new TimelineMax({
      onComplete: () => {
        // Throw away cloned geometry, material and mesh of both the active and next shapes
        shapeClone.geometry.dispose()
        shapeClone.material.dispose()
        shape.parent.remove(shapeClone)
        shape.visible = true
      },
      // delay: 2
    })

    this.currentTimeline = timeline

    timeline
      // .fromTo(shape.parent.rotation, 9, { y: 0 }, { y: 4, ease: Power2.easeInOut }, 0)
      .to(animVals, 2, {
        scale: 1,
        ease: Power2.easeOut,
        onUpdate: function () {
          edgeGroup.children[0].material.opacity = animVals.scale
          verticesGroup.children[0].material.opacity = animVals.scale
          edgeGroup.children.forEach(edgeMesh => {
            edgeMesh.scale.y = animVals.scale + 0.00000000001
          })
          verticesGroup.children.forEach(vertexMesh => {
            let scaleTo = animVals.scale + 0.00000000001
            vertexMesh.scale.set(scaleTo, scaleTo, scaleTo)
          })
        }
      }, 1.5)
      // Explosion
      .to(animVals, 1.5, {
        explode: 0,
        ease: Power2.easeOut,
        onUpdate: function () {
          explodeGeometry(shapeClone, 20, animVals.explode)
        }
      }, 2)
      .to(animVals, 1.5, {
        opacity: 1,
        ease: Power0.easeNone,
        onUpdate: function () {
          shapeClone.material.opacity = animVals.opacity
        }
      }, 2)
  }

  /* ======================================================================
   * SCENE INITIALIZATION
   * ====================================================================== */

  init () {
    for (let shapeKey in this.shapeGroups) {
      let shapeGroup = this.shapeGroups[shapeKey]
      let shape = shapeGroup.getObjectByName('shape')

      explodeModifier.modify(shape.geometry)
      storeOriginalVertices(shape)
      storeOriginalCentroids(shape)

      let verticesGeometry = addSpheresToVerticesLite(shape)
      let cylindersGeometry = addCylindersToEdgesLite(shape)
      verticesGeometry.merge(cylindersGeometry)
      verticesGeometry.mergeVertices()
      let wireframe = new Mesh(verticesGeometry, new MeshLambertMaterial({ color: 0x1a2140, transparent: true }))
      wireframe.name = 'wireframe'
      shapeGroup.add(wireframe)

      // add shape group to base
      this.baseGroup.add(shapeGroup)
    }

    this.scene.add(this.rotationGroup)

    this.renderer.setClearColor(0x0b0f1d, 0)
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.container.appendChild(this.renderer.domElement)

    $(window).resize(() => {
      this.camera.aspect = window.innerWidth / window.innerHeight
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(window.innerWidth, window.innerHeight)
    })

    $(window).mousemove(event => {
      if (!this.allowCameraRotation) return
      let x = ((event.clientX - (window.innerWidth / 2)) / window.innerWidth) * 2
      let y = ((event.clientY - (window.innerHeight / 2)) / window.innerHeight) * 2
      TweenMax.to(this.rotationGroup.rotation, 0.6, { x: y * -0.25 })
      TweenMax.to(this.camera.position, 0.6, { z: 30 + (((x + 1) / 2) * -2.5) })
    })

    // this.playIntroAnimation()
    TweenMax.to($('body'), 1, { opacity: 1, delay: 1 })
    this.render()
  }
}

export default Scene3dAnimation



// WEBPACK FOOTER //
// ./src/lib/Scene3dAnimation.js